import { ArrowDown, Clock, Shield, Phone, Users } from "lucide-react";

const HeroToiture = () => {
  const whatsappNumber = "32493762966";
  const whatsappMessage = encodeURIComponent(
    "Bonjour, je souhaite un devis pour ma toiture. Pouvez-vous me recontacter ?"
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const handleCallClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'click_call' });
    }
  };

  const handleWhatsAppClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'click_whatsapp' });
    }
  };

  return (
    <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/flagged/photo-1566838616631-f2618f74a6a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3VzZSUyMHJvb2YlMjBleHRlcmlvciUyMHNvbGFyfGVufDB8fHx8MTc3NTgwMDcyMHww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="max-w-3xl">
          {/* Expert Badge */}
          <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm text-amber-400 px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-fade-in border border-amber-500/30">
            <Shield className="w-4 h-4" />
            Experts en rénovation énergétique depuis 2018
          </div>

          {/* Headline */}
          <h1 
            className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in"
            style={{ animationDelay: '0.1s' }}
          >
            Votre toiture réparée ou rénovée par des{" "}
            <span className="text-amber-500">experts en Wallonie</span>
          </h1>

          {/* Subline */}
          <p 
            className="text-lg sm:text-xl text-slate-300 mb-4 leading-relaxed animate-fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            Réparation, remplacement, isolation — <span className="text-white font-medium">Devis gratuit sous 24h.</span>
          </p>
          
          <p 
            className="text-base sm:text-lg text-slate-400 mb-6 sm:mb-8 animate-fade-in"
            style={{ animationDelay: '0.25s' }}
          >
            Garantie décennale · Intervention rapide en Wallonie & Brabant Wallon
          </p>

          {/* Urgency Banner */}
          <div 
            className="bg-red-500/20 border border-red-500/40 rounded-xl p-4 mb-6 sm:mb-8 animate-fade-in"
            style={{ animationDelay: '0.3s' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Urgence toiture ? Intervention sous 24h</p>
                <p className="text-red-200 text-sm">Fuite, tempête, infiltration — Nous intervenons rapidement</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons - Mobile optimized */}
          <div 
            className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4 mb-8 sm:mb-10 animate-fade-in"
            style={{ animationDelay: '0.35s' }}
          >
            <a
              href="#form"
              className="group inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-600 text-white px-6 sm:px-8 py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 hover:-translate-y-1 shadow-xl shadow-amber-500/30"
              data-testid="hero-cta-button"
            >
              Demander mon devis gratuit
              <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
            </a>
            
            <a
              href="tel:+3264232850"
              onClick={handleCallClick}
              className="inline-flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 text-white px-6 py-4 rounded-full font-semibold transition-all duration-300 border border-white/20"
              data-testid="hero-phone-button"
            >
              <Phone className="w-5 h-5" />
              Appeler
            </a>
            
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-green-500/25"
              data-testid="hero-whatsapp-button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>

          {/* Trust Stats */}
          <div 
            className="flex flex-wrap items-center gap-4 sm:gap-6 lg:gap-8 animate-fade-in"
            style={{ animationDelay: '0.4s' }}
          >
            <div className="flex items-center gap-3">
              <div className="text-2xl sm:text-3xl font-heading font-bold text-white">350+</div>
              <div className="text-xs sm:text-sm text-slate-400 leading-tight">projets<br />réalisés</div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-slate-600 hidden sm:block"></div>
            <div className="flex items-center gap-3">
              <div className="text-2xl sm:text-3xl font-heading font-bold text-amber-500">4.9/5</div>
              <div className="text-xs sm:text-sm text-slate-400 leading-tight">Google Avis<br />91 avis</div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-slate-600 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <Users className="w-5 sm:w-6 h-5 sm:h-6 text-slate-400" />
              <div className="text-xs sm:text-sm text-slate-400">
                <span className="text-white font-medium">Accompagnement</span><br />personnalisé
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <a href="#trust" className="text-white/60 hover:text-white transition-colors">
          <ArrowDown className="w-6 h-6" />
        </a>
      </div>
    </section>
  );
};

export default HeroToiture;
